import { useState } from 'react';
import { VStack } from '@terra/ui-styled-system';
import {
  Button,
  Field,
  Input,
  SettingsBooleanControl,
  SettingsEditDialog,
  SettingsRow,
} from '@terra/ui-design-system';
import { Add01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useSettingsHelpers } from '../hooks/useSettingsHelpers';
import { ProxySettingsGroup } from './settings/proxy-settings/ProxySettingsGroup';
import { renderProxySettingsListRows } from './settings/proxy-settings/ProxySettingsList';
import { ProxyValueAddDialog } from './settings/proxy-settings/ProxyValueAddDialog';

const EMPTY_CUSTOM_HEADER = { name: '', value: '' };

export function HeadersTab() {
  const t = useTranslations('settings.proxyForm.headers');
  const tHeaders = useTranslations('settings.proxyHeadersActions');
  const tCommon = useTranslations('common');
  const {
    settings,
    updateSetting,
    updateNestedSetting,
    addToArraySetting,
    removeFromArraySetting,
  } = useSettingsHelpers();
  const [isCustomHeaderDialogOpen, setIsCustomHeaderDialogOpen] =
    useState(false);
  const [isRemoveHeaderDialogOpen, setIsRemoveHeaderDialogOpen] =
    useState(false);
  const [customHeaderForm, setCustomHeaderForm] = useState(EMPTY_CUSTOM_HEADER);
  const customHeaderEntries = Object.entries(settings.customHeaders || {});
  const removeHeaders = settings.removeHeaders || [];

  const closeCustomHeaderDialog = () => {
    setCustomHeaderForm(EMPTY_CUSTOM_HEADER);
    setIsCustomHeaderDialogOpen(false);
  };

  const handleAddCustomHeader = () => {
    const name = customHeaderForm.name.trim();
    const value = customHeaderForm.value.trim();

    if (!name || !value) {
      return;
    }

    updateNestedSetting('customHeaders', name, value);
    closeCustomHeaderDialog();
  };

  const handleRemoveCustomHeader = (headerName: string) => {
    updateSetting(
      'customHeaders',
      Object.fromEntries(
        customHeaderEntries.filter(([name]) => name !== headerName),
      ),
    );
  };

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      <ProxySettingsGroup>
        <SettingsRow
          label={t('enabled')}
          description={t('enabledDescription')}
          control={
            <SettingsBooleanControl
              checked={settings.modifyHeaders}
              onCheckedChange={(details) =>
                updateSetting('modifyHeaders', details.checked)
              }
            />
          }
        />
      </ProxySettingsGroup>

      {settings.modifyHeaders ? (
        <ProxySettingsGroup
          title={t('customGroup')}
          description={t('customGroupDescription')}
          action={
            <Button
              size="xs"
              variant="outline"
              onClick={() => setIsCustomHeaderDialogOpen(true)}
            >
              <Add01Icon size={14} />
              {t('addHeader')}
            </Button>
          }
        >
          {renderProxySettingsListRows({
            items: customHeaderEntries.map(([name, value]) => ({
              key: name,
              label: name,
              value: String(value),
            })),
            emptyText: t('customEmpty'),
            removeLabel: tHeaders('removeCustomHeader'),
            onRemove: handleRemoveCustomHeader,
          })}
        </ProxySettingsGroup>
      ) : null}

      {settings.modifyHeaders ? (
        <ProxySettingsGroup
          title={t('removeGroup')}
          description={t('removeGroupDescription')}
          action={
            <Button
              size="xs"
              variant="outline"
              onClick={() => setIsRemoveHeaderDialogOpen(true)}
            >
              <Add01Icon size={14} />
              {t('addHeader')}
            </Button>
          }
        >
          {renderProxySettingsListRows({
            items: removeHeaders.map((headerName, index) => ({
              key: String(index),
              label: headerName,
            })),
            emptyText: t('removeEmpty'),
            removeLabel: tHeaders('removeHeader'),
            onRemove: (key) =>
              removeFromArraySetting('removeHeaders', Number(key)),
          })}
        </ProxySettingsGroup>
      ) : null}

      <SettingsEditDialog
        open={isCustomHeaderDialogOpen}
        title={t('addCustomTitle')}
        description={t('customGroupDescription')}
        onOpenChange={(open) => {
          if (!open) closeCustomHeaderDialog();
        }}
        onSubmit={handleAddCustomHeader}
        submitText={t('addHeader')}
        cancelText={tCommon('cancel')}
        isSubmitDisabled={
          !customHeaderForm.name.trim() || !customHeaderForm.value.trim()
        }
      >
        <VStack gap={3} alignItems="stretch" w="full">
          <Field.Root>
            <Field.Label>{t('headerName')}</Field.Label>
            <Input
              size="sm"
              value={customHeaderForm.name}
              onChange={(event) =>
                setCustomHeaderForm((previous) => ({
                  ...previous,
                  name: event.target.value,
                }))
              }
              placeholder={t('headerNamePlaceholder')}
            />
          </Field.Root>
          <Field.Root>
            <Field.Label>{t('headerValue')}</Field.Label>
            <Input
              size="sm"
              value={customHeaderForm.value}
              onChange={(event) =>
                setCustomHeaderForm((previous) => ({
                  ...previous,
                  value: event.target.value,
                }))
              }
              placeholder={t('headerValuePlaceholder')}
            />
          </Field.Root>
        </VStack>
      </SettingsEditDialog>

      <ProxyValueAddDialog
        open={isRemoveHeaderDialogOpen}
        title={t('addRemoveTitle')}
        description={t('removeGroupDescription')}
        label={t('headerName')}
        placeholder={t('removeNamePlaceholder')}
        onAdd={(name) => addToArraySetting('removeHeaders', name)}
        onClose={() => setIsRemoveHeaderDialogOpen(false)}
      />
    </VStack>
  );
}
